import Input from "../../components/Input/Input"
import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import Button from "../../components/Button/Button"
import { NavLink, useNavigate, useParams } from "react-router-dom"
import { areFieldsNotEmpty } from "../../helpers/helper"
import { phoneNumber } from "../../utils/payment"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import ContainerPage from "../../components/ContainerPage/ContainerPage"
import PageTitle from "../../components/PageTitle/PageTitle"
import Calendar from "../../components/Calendar/Calendar"
import { serverToLocalDateOnly } from "../../utils/date"
import { getAllUsersSubRolesDropdown, getUsersById, updateUsers } from "../../features/users/users"
import { clearListCache } from "../../utils/listCache"
import { fetchContactGroupsDropdownInfo } from "../../features/contactGroups/contactGroups"
import Dropdown from "../../components/Dropdown/Dropdown"
import Card from "../../components/Card/Card"
import BrandAccessPanel from "../../components/BrandAccess/BrandAccessPanel"
import EmailSettingsPanel from "../../components/EmailSettings/EmailSettingsPanel"
import AccountingDailyEmailSwitch from "../../components/AccountingDailyEmailSwitch/AccountingDailyEmailSwitch"
import { StorageGetItem, USER } from "../../lib/storage"

const EditUsers = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { triggerLoading } = useMyContextLoading()
  const currentURL = window.location.href
  const parts = currentURL.split('/')
  const userId = parts[parts.length - 1]

  const [reqError, setReqError] = useState('')
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  })
  const [subRoles, setSubRoles] = useState([])
  const [departments, setDepartments] = useState([])
  const [isFormValid, setIsFormValid] = useState(false)
  const [homeCompany, setHomeCompany] = useState('')
  const [userRole, setUserRole] = useState('')

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
    })
  }, [])

  useEffect(() => {
    const handleReqError = () => {
      reqError.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }

    handleReqError()
  }, [reqError])

  const handleEditUsers = async () => {
    let newNumber = formData?.primaryPhone
    triggerLoading(true)
    if (newNumber) {
      newNumber = formData.primaryPhone?.replace(/\D/g, "")
    }

    const response = await dispatch(updateUsers({
      userData: { ...formData, primaryPhone: newNumber }, userId
    }))
    if (response.error) {
      setReqError(response.payload)
    } else if (response) {
      showSnackBar("success", "Your changes are saved")
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
      }
      )
      clearListCache('users')
      navigate('/users')
    }
    triggerLoading(false)
  }


  const handleInputChange = ({ name, value }) => {
    switch (name) {
      case 'primaryPhone':
        setFormData((prevData) => ({ ...prevData, [name]: phoneNumber(value) }))
        break
      default:
        const [parentField, childField] = name.split('.')
        if (childField) {
          setFormData((prevData) => ({
            ...prevData,
            [parentField]: {
              ...prevData[parentField],
              [childField]: value
            }
          }))
        } else {
          setFormData((prevData) => ({
            ...prevData,
            [name]: value
          }))
        }
        break
    }
  }

  const getSubRoles = async () => {
    triggerLoading(true)
    const response = await dispatch(getAllUsersSubRolesDropdown())
    if (response.error) {
      showSnackBar("error", response?.payload || "Something went wrong")
    } else {
      setSubRoles(response?.payload?.data)
    }
    triggerLoading(false)
  }

  const getDepartments = async () => {
    const response = await dispatch(fetchContactGroupsDropdownInfo())
    if (response.error) {
      showSnackBar("error", response?.payload || "Something went wrong")
    } else {
      setDepartments(response?.payload?.data?.filter(group => group.isDepartment === true) || [])
    }
  }

  const getUsersByIdReq = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getUsersById(userId))
      const user = response?.payload?.data
      setHomeCompany(user?.company || '')

      const matchedSubRole = subRoles.find((item) => item.label === user?.subRole?.label)
      setFormData({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        primaryPhone: phoneNumber(user?.primaryPhone) || '',
        address: {
          address: user?.address?.address || '',
          aptNumber: user?.address?.aptNumber || '',
          city: user?.address?.city || '',
          state: user?.address?.state || '',
          zip: user?.address?.zip || '',
        },
        dateOfBirth: serverToLocalDateOnly(user?.dateOfBirth) || '',
        subRole: matchedSubRole?.value || null,
        contactGroups: user?.contactGroups?.length
          ? [user.contactGroups[0]?._id || user.contactGroups[0]]
          : [],
      })
    } catch (error) {
      console.error(error)
      setReqError(error.message || 'Failed to get customer data')
    }
    triggerLoading(false)
  }

  useEffect(() => {
    const checkFormValidity = () => {
      setIsFormValid(
        areFieldsNotEmpty([formData.firstName, formData.lastName, formData.email]) &&
        formData.dateOfBirth !== ""
      )
    }

    checkFormValidity()
  }, [formData])

  useEffect(() => {
    getSubRoles()
    getDepartments()
  }, [])

  useEffect(() => {
    if (subRoles.length > 0) {
      getUsersByIdReq()
    }
  }, [subRoles])
  return (
    <ContainerPage>
      <div className="flex justify-between items-center">
        <PageTitle title="Edit user" backTo="/users"
          subtitle={[formData?.firstName, formData?.lastName].filter(Boolean).join(' ')} />
      </div>
      <div className="flex  w-full items-center justify-center">
        <Card className="w-full max-w-[850px] p-12 border border-divider">
          <div className="grid grid-cols-2 gap-x-4 gap-y-6 pb-6 h-max border-b border-divider ">
            <Input
              width="w-full"
              placeholder="Enter First Name"
              label="First name *"
              value={formData.firstName}
              onChange={(e) => handleInputChange({ name: "firstName", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter Last Name"
              label="Last name *"
              value={formData.lastName}
              onChange={(e) => handleInputChange({ name: "lastName", value: e.target.value })}
            />
            <Input
              type="email"
              width="w-full"
              placeholder="Enter Email"
              label="Email *"
              value={formData.email}
              onChange={(e) => handleInputChange({ name: "email", value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Enter Primary Phone"
              label="Primary Phone"
              value={formData.primaryPhone}
              onChange={(e) => handleInputChange({ name: "primaryPhone", value: e.target.value })}
            />
            <Calendar
              width="w-full"
              label="Date Of Birth *"
              height="56px"
              value={formData.dateOfBirth}
              onChange={(e) => handleInputChange({ name: "dateOfBirth", value: e })}
            />
            <Dropdown
              value={formData?.subRole}
              setValue={(e) => handleInputChange({ name: "subRole", value: e })}
              menuItems={subRoles}
              className="w-full mt-1"
              variant="standard"
              placeholder="Role"
              label="Role"
            />
            <Dropdown
              value={formData?.contactGroups?.[0] || ''}
              setValue={(e) => handleInputChange({ name: "contactGroups", value: e ? [e] : [] })}
              menuItems={departments}
              className="w-full mt-1"
              variant="standard"
              placeholder="Department"
              label="Department"
            />
            {userRole === 'customer' && <AccountingDailyEmailSwitch userId={userId} />}
          </div>
          <div className={`mt-6 flex justify-between items-center`}>
            <Button
              onClick={() => {
                const email = encodeURIComponent(formData.email || '')
                navigate(`/admin/reset-password?userId=${userId}&userType=user&email=${email}`)
              }}
              type="outlined"
              size="lg"
              width="min-w-[156px]"
            >
              Reset Password
            </Button>
            <div className="flex space-x-4">
              <NavLink to={"/users"}>
                <Button classes={"mr-4"} onClick={() => {
                }} type="outlined" size="lg" width="min-w-[156px]">Close</Button>
              </NavLink>
              <Button
                onClick={handleEditUsers}
                size="lg"
                width="min-w-[156px]"
                disabled={!isFormValid}
                classes="rounded-lg"
              >
                Save
              </Button>
            </div>
          </div>
        </Card>
      </div>
      <div className="flex w-full items-center justify-center">
        <div className="w-full max-w-[850px]">
          <BrandAccessPanel userId={userId} homeCompany={homeCompany} />
          <EmailSettingsPanel userId={userId} />
        </div>
      </div>
    </ContainerPage>

  )
}

export default EditUsers
